import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import type { TrackEvent } from "@/types/generated";
import { formatLapTime } from "../ui/lap-time";

interface SessionDetailsCardProps {
  trackEvent: TrackEvent;
}

function formatDuration(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function SessionDetailsCard({ trackEvent }: SessionDetailsCardProps) {
  const userBestLapMs = trackEvent.best_lap_time_ms;
  const sessionFastestLapMs = trackEvent.session_fastest_lap_ms;
  const hasMultiDriverData =
    sessionFastestLapMs &&
    userBestLapMs &&
    sessionFastestLapMs !== userBestLapMs;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-base">Session Details</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          <div>
            <div className="text-sm text-muted-foreground mb-0.5">Track</div>
            <div className="font-semibold">{trackEvent.track.name}</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground mb-0.5">Car</div>
            <div className="font-semibold">
              {trackEvent.sim_car?.car_name || "N/A"}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground mb-0.5">Session</div>
            <div className="font-semibold capitalize">
              {trackEvent.session_type}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
          <div>
            <div className="text-sm text-muted-foreground">Laps</div>
            <div className="text-lg font-semibold font-ocr">
              {trackEvent.lap_count ?? "—"}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Your Best Lap</div>
            <div className="text-lg font-semibold font-ocr">
              {trackEvent.best_lap_time_ms
                ? formatLapTime(trackEvent.best_lap_time_ms, true)
                : "—"}
            </div>
            {hasMultiDriverData && sessionFastestLapMs && (
              <div className="text-xs text-muted-foreground mt-0.5">
                Session best: {formatLapTime(sessionFastestLapMs, true)}
                {userBestLapMs && (
                  <span className="text-destructive ml-1">
                    (+
                    {((userBestLapMs - sessionFastestLapMs) / 1000).toFixed(3)}
                    s)
                  </span>
                )}
              </div>
            )}
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Duration</div>
            <div className="text-lg font-semibold font-ocr">
              {trackEvent.duration_seconds
                ? formatDuration(trackEvent.duration_seconds)
                : "—"}
            </div>
          </div>
          {trackEvent.session_type === "race" && (
            <div>
              <div className="text-sm text-muted-foreground">Position</div>
              <div className="text-lg font-semibold font-ocr">
                {trackEvent.finish_position && trackEvent.total_drivers
                  ? `${trackEvent.finish_position}/${trackEvent.total_drivers}`
                  : "—"}
              </div>
            </div>
          )}
        </div>

        {(() => {
          if (!trackEvent.weather_conditions) return null;

          const wc = trackEvent.weather_conditions;
          const airTemp = Number(wc.air_temp);
          const trackTemp = Number(wc.track_temp);
          const humidity = Number(wc.relative_humidity);
          const usage = Number(wc.track_usage);

          const validAir = !isNaN(airTemp);
          const validTrack = !isNaN(trackTemp);
          const validHum = !isNaN(humidity);
          const validUsage = !isNaN(usage);

          if (!validAir && !validTrack && !validHum && !validUsage) return null;

          return (
            <div className="pt-4 border-t mt-4">
              <div className="text-sm text-muted-foreground mb-2">Conditions</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {validAir && (
                  <div>
                    <div className="text-xs text-muted-foreground">Air Temp</div>
                    <div className="font-medium">{airTemp.toFixed(1)}°C</div>
                  </div>
                )}
                {validTrack && (
                  <div>
                    <div className="text-xs text-muted-foreground">Track Temp</div>
                    <div className="font-medium">{trackTemp.toFixed(1)}°C</div>
                  </div>
                )}
                {validHum && (
                  <div>
                    <div className="text-xs text-muted-foreground">Humidity</div>
                    <div className="font-medium">{humidity.toFixed(0)}%</div>
                  </div>
                )}
                {validUsage && (
                  <div>
                    <div className="text-xs text-muted-foreground">Track Usage</div>
                    <div className="font-medium">{usage}%</div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {trackEvent.gear_items && trackEvent.gear_items.length > 0 && (
          <div className="pt-4 border-t mt-4">
            <div className="text-sm text-muted-foreground mb-2">Gear Used</div>
            <div className="flex flex-wrap gap-2">
              {trackEvent.gear_items.map((gear) => (
                <span
                  key={gear.uuid}
                  className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border bg-gray-100 text-gray-700 border-gray-200"
                >
                  {gear.gear_type_label}: {gear.display_name}
                </span>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
